import { useEffect, useRef, useState } from 'react';
import { EventType } from '@azure/msal-browser';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { acceptInvitation, updateProfile } from '../api';

// Invite acceptance page (rendered when the URL is /accept?token=...).
//
// Flow:
//   1. Invitee opens the link. If not signed in, they sign in (or sign up) via
//      the CIAM user flow.
//   2. Once authenticated, we POST the token to /api/invitations/accept. The API
//      takes the email from the VERIFIED token (never the client), and the stored
//      proc binds the invite's email to the token email — a leaked link can't be
//      redeemed under a different identity.
//   3. On success we show a contact/opt-in step: the new member enters their
//      mobile number and actively opts in (unchecked by default) to email and/or
//      SMS announcements. This is the A2P-compliant web-form opt-in of record.
//   4. After they save (or skip), we drop the token from the URL and enter the app.
export default function AcceptInvite({ token }) {
  const { instance } = useMsal();
  // After a loginRedirect, getActiveAccount() stays null until something calls
  // setActiveAccount(); fall back to the signed-in account so redemption can run.
  const account = instance.getActiveAccount() || instance.getAllAccounts()?.[0] || null;
  const accountKey = account?.homeAccountId || account?.username || null;
  const [state, setState] = useState({ phase: 'idle', message: null }); // idle|working|optin|saving|done|error
  const attempted = useRef(false);

  // Opt-in form state — consent boxes MUST default to unchecked (carrier rule).
  const [phone, setPhone] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleLogin = () => {
    // Preserve the token through the redirect by keeping it in the URL.
    instance.loginRedirect(loginRequest).catch((e) => console.error('login failed', e));
  };

  // MSAL does not re-render this component when the redirect completes, so
  // subscribe to login/account events and bump state to force a re-render.
  // On login success, promote the account to active so the rest of the app agrees.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const cbId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        instance.setActiveAccount(event.payload.account);
      }
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.HANDLE_REDIRECT_END ||
        event.eventType === EventType.ACCOUNT_ADDED
      ) {
        forceTick((n) => n + 1);
      }
    });
    return () => { if (cbId) instance.removeEventCallback(cbId); };
  }, [instance]);

  useEffect(() => {
    if (!accountKey) return;          // not signed in yet
    if (!token) return;               // nothing to redeem
    if (attempted.current) return;    // once per mount/account
    attempted.current = true;

    let cancelled = false;
    setState({ phase: 'working', message: null });
    (async () => {
      try {
        const { ok, status, data } = await acceptInvitation(instance, account, token);
        if (cancelled) return;
        if (ok) {
          // Linked — clean the token from the URL now, then collect contact prefs.
          window.history.replaceState({}, '', '/accept');
          setState({
            phase: 'optin',
            message: data?.alreadyMember ? 'You were already linked — welcome back.' : 'You are in! Your membership is linked.',
          });
        } else {
          setState({ phase: 'error', message: data?.message || `Couldn't accept invitation (${status}).` });
        }
      } catch (e) {
        if (!cancelled) setState({ phase: 'error', message: e.message });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey, token]);

  const hasPhone = phone.trim().length > 0;

  function goToApp() {
    window.history.replaceState({}, '', '/');
    window.location.href = '/';
  }

  async function savePrefs() {
    setFormError(null);
    if (smsOptIn && !hasPhone) {
      setFormError('Add a mobile number before opting in to text messages.');
      return;
    }
    setState((s) => ({ ...s, phase: 'saving' }));
    try {
      const { ok, data, status } = await updateProfile(instance, account, {
        phone: phone.trim(),
        emailOptIn,
        smsOptIn: hasPhone ? smsOptIn : false,
      });
      if (ok) {
        goToApp();
      } else {
        setFormError((data && (data.message || data.error)) || `Couldn't save (${status}).`);
        setState((s) => ({ ...s, phase: 'optin' }));
      }
    } catch (e) {
      setFormError(e.message);
      setState((s) => ({ ...s, phase: 'optin' }));
    }
  }

  const saving = state.phase === 'saving';

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">🦅 Eagle Ridge Outdoor Club</div>
      </header>
      <main className="content">
        <div className="card">
          <h2>Accept your invitation</h2>

          {!token && (
            <p className="error-inline">This link is missing its invitation token. Ask your admin to resend it.</p>
          )}

          <UnauthenticatedTemplate>
            {token && (
              <>
                <p>You've been invited to join Eagle Ridge. Sign in or create your account to finish.</p>
                <button className="primary" onClick={handleLogin}>Sign in to accept</button>
              </>
            )}
          </UnauthenticatedTemplate>

          <AuthenticatedTemplate>
            {state.phase === 'working' && <p className="muted">Linking your account…</p>}

            {(state.phase === 'optin' || state.phase === 'saving') && (
              <>
                <p className="notice">{state.message}</p>
                <h3>Stay in the loop</h3>
                <p className="muted">
                  Choose how the club can reach you with announcements and updates. This is optional — you can change it any time in your profile.
                </p>

                {formError && <div className="notice warn">{formError}</div>}

                <div className="prefs-form">
                  <label className="field">
                    <span>Mobile phone</span>
                    <input
                      type="tel"
                      placeholder="502-555-1234"
                      value={phone}
                      disabled={saving}
                      onChange={(e) => setPhone(e.target.value)}
                      onBlur={() => { if (!phone.trim()) setSmsOptIn(false); }}
                    />
                  </label>

                  <label className="checkbox">
                    <input type="checkbox" checked={emailOptIn} disabled={saving}
                      onChange={(e) => setEmailOptIn(e.target.checked)} />
                    <span>Email me club announcements and updates</span>
                  </label>

                  <label className={`checkbox${!hasPhone ? ' disabled' : ''}`}>
                    <input type="checkbox" checked={smsOptIn && hasPhone} disabled={saving || !hasPhone}
                      onChange={(e) => setSmsOptIn(e.target.checked)} />
                    <span>
                      Text me club announcements (SMS)
                      {!hasPhone && <em className="muted"> — add a phone number first</em>}
                    </span>
                  </label>

                  <p className="muted fine-print">
                    By checking "Text me club announcements" you agree to receive informational SMS from Eagle Ridge.
                    Message frequency varies (a few messages per month). Message and data rates may apply.
                    Reply STOP to opt out, HELP for help. See our{' '}
                    <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and{' '}
                    <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                  </p>

                  <div className="btn-row">
                    <button className="primary" disabled={saving} onClick={savePrefs}>
                      {saving ? 'Saving…' : 'Yes, sign me up!'}
                    </button>
                    <button className="link-btn" disabled={saving} onClick={goToApp}>Skip for now</button>
                  </div>
                </div>
              </>
            )}

            {state.phase === 'error' && (
              <>
                <p className="error-inline">{state.message}</p>
                <p className="muted">If this keeps happening, ask your admin to reissue the invite.</p>
                <button onClick={goToApp}>Go to the app</button>
              </>
            )}
          </AuthenticatedTemplate>
        </div>
      </main>
      <footer className="footer"><span>Corinth, Kentucky</span></footer>
    </div>
  );
}
